import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bot, Play, Square } from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  role: string;
  description: string;
  status: 'idle' | 'working' | 'completed' | 'error';
  model: string;
  skills: string[];
}

export interface AgentCardProps {
  agent: Agent;
  onStart?: () => void;
  onStop?: () => void;
}

export function AgentCard({ agent, onStart, onStop }: AgentCardProps) {
  const statusColor = {
    idle: 'bg-gray-500/20 text-gray-700',
    working: 'bg-green-500/20 text-green-700',
    completed: 'bg-blue-500/20 text-blue-700',
    error: 'bg-red-500/20 text-red-700',
  }[agent.status];

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Bot className="h-6 w-6 text-primary" />
            <div>
              <CardTitle className="text-lg">{agent.name}</CardTitle>
              <CardDescription>{agent.role}</CardDescription>
            </div>
          </div>
          <Badge className={statusColor}>{agent.status}</Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{agent.description}</p>
        <div className="flex flex-wrap gap-1">
          {agent.skills?.slice(0, 4).map((skill) => (
            <Badge key={skill} variant="secondary" className="text-xs">{skill}</Badge>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          {agent.status !== 'working' ? (
            <Button onClick={onStart} className="flex-1 gap-2">
              <Play className="h-4 w-4" /> Start
            </Button>
          ) : (
            <Button onClick={onStop} variant="outline" className="flex-1 gap-2">
              <Square className="h-4 w-4" /> Stop
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
